class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');
    this.popularSuggestions = this.querySelector('[data-popular-suggestions]');

    if (this.input) {
      this.input.form.addEventListener('reset', this.onFormReset.bind(this));
      this.input.form.addEventListener('formdata', this.onFormData.bind(this));
      this.input.addEventListener('input', this.updateInputState.bind(this));
      this.input.addEventListener(
        'input',
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this)
      );
      this.input.addEventListener('focus', this.onInputFocus.bind(this));
      this.input.addEventListener('blur', this.onInputBlur.bind(this));
      this.updateInputState();
    }

    this._onScrollClose = () => {
      if (this.hasAttribute('popular-open')) {
        this.input.blur();
        this.hidePopularSuggestions();
      }
    };
    window.addEventListener('scroll', this._onScrollClose, { passive: true });

    if (this.popularSuggestions) {
      this.popularSuggestions.addEventListener('mousedown', (event) => event.preventDefault());
      this.popularSuggestions.querySelectorAll('[data-suggestion]').forEach((button) => {
        button.addEventListener('click', this.onSuggestionClick.bind(this));
      });
    }
  }

  updateInputState() {
    this.input.classList.toggle('field__input--has-value', this.input.value.length > 0);
  }

  onInputFocus() {
    if (!this.getQuery().length) {
      this.showPopularSuggestions();
    }
  }

  onInputBlur() {
    if (document.body.classList.contains('header-search-sheet-open') && !this.getQuery().length) {
      return;
    }

    setTimeout(() => {
      if (document.body.classList.contains('header-search-sheet-open') && !this.getQuery().length) {
        return;
      }

      this.hidePopularSuggestions();
    }, 150);
  }

  onSuggestionClick(event) {
    const term = event.currentTarget.dataset.suggestion;
    if (!term) return;

    this.input.value = term;
    this.updateInputState();
    this.toggleResetButton();
    this.hidePopularSuggestions();

    if (typeof this.onSuggestionSelected === 'function') {
      this.onSuggestionSelected(term);
      return;
    }

    this.input.form.requestSubmit();
  }

  getQuery() {
    return this.input.value.trim();
  }

  onFormData(event) {
    // Expand design codes, optionally with a product suffix; preserve normal queries.
    const query = event.formData.get('q')?.trim();
    if (query && /^[a-z]+\d+(?:_[a-z0-9]+)?$/i.test(query)) {
      event.formData.set('q', `${query}*`);
    }
  }

  showPopularSuggestions() {
    if (!this.popularSuggestions || this.getQuery().length > 0) return;

    this.popularSuggestions.removeAttribute('hidden');
    this.setAttribute('popular-open', true);
    this.input.setAttribute('aria-expanded', 'true');
    document.body.classList.add('search-popular-suggestions-open');
  }

  hidePopularSuggestions() {
    if (!this.popularSuggestions) return;

    this.popularSuggestions.setAttribute('hidden', '');
    this.removeAttribute('popular-open');

    if (!document.querySelector('search-form[popular-open], predictive-search[popular-open]')) {
      document.body.classList.remove('search-popular-suggestions-open');
    }

    if (!this.hasAttribute('open')) {
      this.input.setAttribute('aria-expanded', 'false');
    }
  }

  toggleResetButton() {
    const resetIsHidden = this.resetButton.classList.contains('hidden');
    if (this.input.value.length > 0 && resetIsHidden) {
      this.resetButton.classList.remove('hidden');
    } else if (this.input.value.length === 0 && !resetIsHidden) {
      this.resetButton.classList.add('hidden');
    }
  }

  onChange() {
    this.toggleResetButton();

    if (this.getQuery().length) {
      this.hidePopularSuggestions();
    } else if (document.activeElement === this.input) {
      this.showPopularSuggestions();
    }
  }

  shouldResetForm() {
    return !document.querySelector('[aria-selected="true"] a');
  }

  onFormReset(event) {
    // Prevent default so the form reset doesn't set the value gotten from the url on page load
    event.preventDefault();
    // Don't reset if the user has selected an element on the predictive search dropdown
    if (this.shouldResetForm()) {
      this.input.value = '';
      this.input.focus();
      this.toggleResetButton();
      this.updateInputState();
      this.showPopularSuggestions();
    }
  }
  disconnectedCallback() {
    window.removeEventListener('scroll', this._onScrollClose);
    if (!document.querySelector('search-form[popular-open], predictive-search[popular-open]')) {
      document.body.classList.remove('search-popular-suggestions-open');
    }
  }
}

customElements.define('search-form', SearchForm);
